import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client from "@/lib/api";
import { ArrowRight, Plus, MagnifyingGlass } from "@phosphor-icons/react";

const STATUS_STYLE = {
  Scheduled: "bg-[#FEF3C7] text-[#92400E] border-[#FBBF24]",
  "In Progress": "bg-[#DBEAFE] text-[#1E40AF] border-[#60A5FA]",
  Completed: "bg-[#D1FAE5] text-[#065F46] border-[#34D399]",
};

const TYPES = ["All", "Move-In", "Move-Out", "Mid-Term", "Video Walk-Around"];

export default function Inspections() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("All");
  const [type, setType] = useState("All");
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [properties, setProperties] = useState({});

  const load = useCallback(async () => {
    const params = {};
    if (status !== "All") params.status = status;
    if (type !== "All") params.inspection_type = type;
    if (q) params.q = q;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    const [i, p] = await Promise.all([
      client.get("/inspections", { params }),
      client.get("/properties"),
    ]);
    setItems(i.data);
    const map = {};
    p.data.forEach((x) => (map[x.id] = x));
    setProperties(map);
  }, [status, type, q, dateFrom, dateTo]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div data-testid="inspections-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#6B7280] mb-1">Operations</div>
          <h1 className="trent-heading text-3xl md:text-4xl font-black tracking-tighter text-[#1E3A5F]">
            Inspections
          </h1>
        </div>
        <Link to="/admin-dashboard/inspections/new" className="trent-btn-primary" data-testid="new-inspection-cta">
          <Plus weight="bold" size={16} /> New inspection
        </Link>
      </div>

      <div className="trent-card p-4 mb-4 grid md:grid-cols-5 gap-3 items-end">
        <label className="md:col-span-2 block">
          <span className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">Search</span>
          <div className="relative mt-1">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" size={16} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Address, Eircode, inspector…"
              className="trent-input pl-9"
              data-testid="inspection-search"
            />
          </div>
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">Type</span>
          <select value={type} onChange={(e) => setType(e.target.value)} className="trent-input mt-1" data-testid="filter-type">
            {TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">From</span>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="trent-input mt-1" data-testid="filter-date-from" />
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">To</span>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="trent-input mt-1" data-testid="filter-date-to" />
        </label>
      </div>

      <div className="flex gap-1 mb-4 border-b border-[#E5E7EB]">
        {["All", "Scheduled", "In Progress", "Completed"].map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            data-testid={`filter-${s.replace(/\s+/g, "-").toLowerCase()}`}
            className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider border-b-2 ${
              status === s
                ? "border-[#16A34A] text-[#1E3A5F]"
                : "border-transparent text-[#6B7280] hover:text-[#1E3A5F]"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      <div className="trent-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F3F4F6] text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">
            <tr>
              <th className="text-left px-4 py-3">Property</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Type</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Inspector</th>
              <th className="text-left px-4 py-3">Date</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="px-4 py-3 w-16"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center text-[#6B7280] py-12">
                  No inspections match your filters.
                </td>
              </tr>
            )}
            {items.map((i) => (
              <tr key={i.id} className="border-t border-[#E5E7EB] hover:bg-[#FAFAF7]" data-testid={`inspection-row-${i.id}`}>
                <td className="px-4 py-3 font-semibold text-[#1E3A5F]">
                  {properties[i.property_id]?.address || "—"}
                </td>
                <td className="px-4 py-3 hidden md:table-cell">{i.inspection_type}</td>
                <td className="px-4 py-3 hidden md:table-cell text-[#6B7280]">{i.inspector_name}</td>
                <td className="px-4 py-3 text-[#6B7280]">{i.inspection_date}</td>
                <td className="px-4 py-3">
                  <span className={`condition-pill ${STATUS_STYLE[i.status] || ""}`}>{i.status}</span>
                </td>
                <td className="px-4 py-3 text-right">
                  <Link to={`/admin-dashboard/inspections/${i.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-[#1E3A5F] uppercase tracking-wider hover:text-[#16A34A]">
                    Open <ArrowRight size={12} weight="bold" />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-6">
        <Link to="/admin-dashboard/compare" className="text-xs font-semibold uppercase tracking-wider text-[#1E3A5F] underline" data-testid="compare-link">
          Compare move-in vs move-out →
        </Link>
      </div>
    </div>
  );
}
